// Dashboard API client — Phase 4 Plan 04-05 (DIS-14, D-73).
//
// Wraps:
//   GET  /api/v1/dashboard/state      — JSON snapshot
//   POST /api/v1/dashboard/sse-token  — mint short-lived SSE ticket (CR-05)
//   GET  /api/v1/dashboard/events     — SSE stream (text/event-stream)
//
// EventSource auth (CR-05 fix): browsers cannot set custom headers on
// EventSource. Previously we passed the JWT via `?token=<jwt>` query — but
// that leaked the bearer credential into Caddy access logs, browser history,
// and Referer headers on any third-party fetch (HERE Maps tiles in
// particular).
//
// New flow:
//   1. POST /api/v1/dashboard/sse-token with Authorization: Bearer <jwt>
//      header → returns { ticket, expires_in }.
//   2. Open `EventSource(?ticket=<ticket>)`. The ticket is a 256-bit random
//      string single-use bound to the caller's JWT, expires in 60s, and is
//      Redis-validated server-side (atomic GET+DEL).
//
// Even if the ticket leaks to logs/history/referer it's already burned by
// the time anyone could replay it.

import type { ApiClient } from "./client";

// ---------------------------------------------------------------------------
// State snapshot shape (mirrors backend dashboard_state_service.DashboardSnapshot)
// ---------------------------------------------------------------------------

export interface DashboardLastGps {
  lat: number;
  lng: number;
  recorded_at: string;
}

export interface DashboardTrip {
  id: number;
  driver_id: number | null;
  driver_name: string | null;
  status: string;
  gps_status: string | null;
  last_gps: DashboardLastGps | null;
  eta_arrival_at: string | null;
  has_open_problem: boolean;
}

export interface DashboardEvent {
  id: number;
  trip_id: number;
  event_type: string;
  occurred_at: string;
}

export interface DashboardProblem {
  id: number;
  trip_id: number;
  problem_type: string;
  severity: string;
  created_at: string;
}

export interface DashboardKpis {
  active: number;
  problems: number;
  on_time_pct: number | null;
  late: number;
}

export interface DashboardState {
  trips: DashboardTrip[];
  events: DashboardEvent[];
  problems: DashboardProblem[];
  kpis: DashboardKpis;
}

// ---------------------------------------------------------------------------
// SSE event payload shape (mirrors backend dashboard_pubsub event)
// ---------------------------------------------------------------------------

export interface SseGpsUpdate {
  event_id: string;
  type: "gps_update";
  data: {
    trip_id: number;
    lat: number;
    lng: number;
    recorded_at: string;
  };
}

export interface SseAlert {
  event_id: string;
  type: "alert";
  severity: "warning" | "destructive";
  data: {
    alert_type: "driver_offline_1h" | "overdue_2h" | "problem_open";
    trip_id: number;
    driver_id: number | null;
    severity: "warning" | "destructive";
  };
}

export interface SseTripEvent {
  event_id: string;
  type: "trip_event";
  data: {
    trip_id: number;
    event_type: string;
    occurred_at: string;
  };
}

export type DashboardSseEvent = SseGpsUpdate | SseAlert | SseTripEvent;

// ---------------------------------------------------------------------------
// API surface
// ---------------------------------------------------------------------------

export interface SseTicketResponse {
  ticket: string;
  expires_in: number;
}

export const dashboardApi = {
  /** GET /api/v1/dashboard/state — JSON snapshot (initial Mini App load). */
  state(client: ApiClient): Promise<DashboardState> {
    return client.get<DashboardState>("/api/v1/dashboard/state");
  },

  /**
   * POST /api/v1/dashboard/sse-token — mint a one-time SSE ticket bound to
   * the caller's JWT. Returns a short-lived (60s) random string that may
   * safely appear in a URL query (it is single-use and Redis-validated).
   *
   * Replaces the legacy `?token=<jwt>` URL-leak pattern (CR-05).
   */
  sseToken(client: ApiClient): Promise<SseTicketResponse> {
    return client.post<SseTicketResponse>("/api/v1/dashboard/sse-token", {});
  },

  /**
   * Open an EventSource connection to /api/v1/dashboard/events.
   *
   * Auth: `?ticket=<sse_ticket>` — a single-use short-lived secret obtained
   * via `sseToken()`. The server validates via Redis (atomic GET+DEL) so
   * even if the ticket leaks to logs/history/referer it's already burned.
   *
   * Last-Event-ID resync: browser's native EventSource attaches the header
   * automatically on reconnect. We additionally accept it as `?last_event_id=`
   * query for environments without that feature (e.g. test polyfills).
   *
   * Caller is responsible for `.close()` on unmount.
   */
  events(
    baseUrl: string,
    ticket: string | null,
    options: { lastEventId?: string | null } = {},
  ): EventSource {
    const params = new URLSearchParams();
    if (ticket) params.set("ticket", ticket);
    if (options.lastEventId) params.set("last_event_id", options.lastEventId);
    const qs = params.toString();
    const url = `${baseUrl}/api/v1/dashboard/events${qs ? `?${qs}` : ""}`;
    return new EventSource(url, { withCredentials: false });
  },
};

// ---------------------------------------------------------------------------
// DIS-15 client-side color FSM (derives marker color from trip state).
// Same thresholds as TRIP_GPS_AGE_THRESHOLDS (synced with D-74 backend alerts).
// ---------------------------------------------------------------------------

import { TRIP_GPS_AGE_THRESHOLDS, type TripStatusColor } from "../lib/tokens";

export function getMarkerColor(trip: DashboardTrip, now: Date = new Date()): TripStatusColor {
  if (trip.status === "completed") return "gray";
  if (trip.has_open_problem) return "red";
  if (!trip.last_gps) return "red";
  const recordedAt = new Date(trip.last_gps.recorded_at).getTime();
  const ageSec = (now.getTime() - recordedAt) / 1000;
  if (ageSec >= TRIP_GPS_AGE_THRESHOLDS.yellowMaxSeconds) return "red";
  if (ageSec >= TRIP_GPS_AGE_THRESHOLDS.greenMaxSeconds) return "yellow";
  return "green";
}

/**
 * R1 delay detection (UI-SPEC §5) — a SEPARATE signal from `getMarkerColor`.
 *
 * A trip is "delayed" when it has a planned arrival (`eta_arrival_at`) that is now
 * in the past and the trip is not yet completed. This is independent of the GPS-age
 * status color: a delayed trip can still be green/yellow/red. The Dashboard map and
 * TripCard render a gold overlay on top of the base status when this is true — the
 * gold layer NEVER replaces the base status color.
 */
export function isTripDelayed(trip: DashboardTrip, now: Date = new Date()): boolean {
  if (trip.status === "completed" || trip.status === "cancelled") return false;
  if (!trip.eta_arrival_at) return false;
  return new Date(trip.eta_arrival_at).getTime() < now.getTime();
}
